import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HouseLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ADDONS } from "@/lib/addons";
import { useAuth } from "@/context/AuthContext";
import { startCheckout } from "@/lib/checkout";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const DFY_FIELDS = [
  { key: "name", label: "Full name", ph: "Your name" },
  { key: "email", label: "Email", ph: "you@example.com", type: "email" },
  { key: "phone", label: "Contact number", ph: "(555) 123-4567" },
  { key: "experience", label: "Years of experience flipping mobile homes", ph: "e.g. 0, 2, 5+" },
  { key: "social", label: "Social media (optional)", ph: "@handle or link" },
  { key: "budget", label: "Budget for entire flip", ph: "e.g. $15,000" },
  { key: "area", label: "Area interested in flipping (city/state)", ph: "e.g. Atlanta, GA" },
  { key: "distance", label: "Distance willing to travel outside preferred area", ph: "e.g. 50 miles" },
];

export default function Services() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [requestFor, setRequestFor] = useState(null);
  const [reqName, setReqName] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqNote, setReqNote] = useState("");
  const [sending, setSending] = useState(false);
  const [dfyOpen, setDfyOpen] = useState(false);
  const [dfyForm, setDfyForm] = useState({ name: "", email: "", phone: "", experience: "", social: "", budget: "", area: "", distance: "", comments: "" });
  const [dfySending, setDfySending] = useState(false);
  const [dfyApp, setDfyApp] = useState(undefined); // undefined = loading, null = none
  const [communityLink, setCommunityLink] = useState("");

  useEffect(() => {
    if (user) {
      api.get("/dfy/mine").then((r) => setDfyApp(r.data)).catch(() => setDfyApp(null));
      if (user.community_access) {
        api.get("/community/link").then((r) => setCommunityLink(r.data.link)).catch(() => {});
      }
    } else if (user === false) {
      setDfyApp(null);
    }
  }, [user]);

  const openRequest = (a) => {
    setRequestFor(a);
    setReqName(user?.name || "");
    setReqEmail(user?.email || "");
    setReqNote("");
  };

  const submitRequest = async () => {
    if (!reqName || !reqEmail) {
      toast.error("Name and email are required.");
      return;
    }
    setSending(true);
    try {
      await api.post("/service-requests", { service: requestFor.title, name: reqName, email: reqEmail, note: reqNote });
      toast.success("Request sent! Ciara will reach out shortly.");
      setRequestFor(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const openDfy = () => {
    setDfyForm({ ...dfyForm, name: user?.name || "", email: user?.email || "" });
    setDfyOpen(true);
  };

  const submitDfy = async () => {
    const required = ["name", "email", "phone", "experience", "budget", "area", "distance"];
    if (required.some((k) => !dfyForm[k])) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setDfySending(true);
    try {
      await api.post("/dfy/apply", dfyForm);
      toast.success("Application submitted — Ciara will review it and reach out.");
      setDfyOpen(false);
      const { data } = await api.get("/dfy/mine");
      setDfyApp(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDfySending(false);
    }
  };

  const joinCommunity = async () => {
    try {
      await api.post("/community/join");
      toast.success("Welcome to the community!");
      refreshUser();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const darkBtn = "mt-6 inline-flex items-center gap-2 border border-foreground bg-foreground px-6 py-3 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-200 hover:bg-primary hover:border-primary hover:text-primary-foreground";
  const outlineBtn = "mt-6 inline-flex items-center gap-2 border border-foreground px-6 py-3 text-xs uppercase tracking-[0.15em] transition-colors duration-200 hover:bg-foreground hover:text-background";
  const mutedBadge = "mt-6 inline-block border border-border px-6 py-3 text-xs uppercase tracking-[0.15em] text-muted-foreground";
  const accentBadge = "mt-6 inline-block border border-primary px-6 py-3 text-xs uppercase tracking-[0.15em] text-primary";

  return (
    <div className="min-h-screen bg-background" data-testid="services-page">
      <div className="grain-overlay" />
      <header className="border-b border-border bg-[#F9F9F7]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="services-nav-logo" className="flex items-center gap-2">
            <HouseLine size={24} weight="duotone" className="text-primary" />
            <span className="font-heading text-2xl tracking-tight">TTPI Academy</span>
          </Link>
          <Link to="/" data-testid="services-back-home" className="text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors duration-200 hover:text-foreground">
            Back to Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-24">
        <motion.div {...fadeUp} className="mb-16 max-w-2xl">
          <p className="overline-label mb-6">À La Carte Services</p>
          <h1 data-testid="services-heading" className="font-heading text-4xl font-light leading-tight sm:text-5xl">
            Add-ons, whenever you need them.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground">
            Beyond the membership packages, these services can be added as needed.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-px border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
          {ADDONS.map((a, i) => (
            <motion.div
              key={a.title}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              data-testid={`service-card-${i}`}
              className={`flex flex-col p-10 ${a.highlight ? "bg-[#141413] text-[#F4F4F0]" : "bg-white"}`}
            >
              {a.price && <p className="font-heading text-4xl font-light text-[#C49A6C]">{a.price}</p>}
              <h2 className={`font-heading text-2xl ${a.price ? "mt-3" : ""}`}>{a.title}</h2>
              <p className={`mt-3 flex-1 text-sm leading-relaxed ${a.highlight ? "text-[#A3A39C]" : "text-muted-foreground"}`}>{a.text}</p>

              {a.highlight && (
                <button
                  data-testid="consultation-book-button"
                  onClick={() => startCheckout("consultation", user, navigate)}
                  className="mt-6 inline-flex items-center gap-2 self-start border border-[#C49A6C] bg-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-transparent hover:text-[#C49A6C]"
                >
                  Book & Pay {a.price} <ArrowRight size={14} />
                </button>
              )}

              {a.buy && (
                <button
                  data-testid={`service-buy-${i}`}
                  onClick={() => startCheckout(a.buy, user, navigate)}
                  className={darkBtn}
                >
                  {a.buyLabel || `Buy — ${a.price}`} <ArrowRight size={14} />
                </button>
              )}

              {a.dfy && (
                dfyApp === undefined ? null :
                !user ? (
                  <button data-testid={`dfy-apply-${i}`} onClick={() => navigate("/auth")} className={darkBtn}>
                    Apply — $800 <ArrowRight size={14} />
                  </button>
                ) : dfyApp === null ? (
                  <button data-testid={`dfy-apply-${i}`} onClick={openDfy} className={darkBtn}>
                    Apply — $800 <ArrowRight size={14} />
                  </button>
                ) : dfyApp.status === "pending" ? (
                  <span data-testid="dfy-status-pending" className={mutedBadge}>Application Under Review</span>
                ) : dfyApp.status === "approved" ? (
                  <button data-testid={`dfy-pay-${i}`} onClick={() => startCheckout("dfy_service", user, navigate)} className={darkBtn}>
                    Approved — Pay $800 <ArrowRight size={14} />
                  </button>
                ) : dfyApp.status === "paid" ? (
                  <span data-testid="dfy-status-paid" className={accentBadge}>Active — 30-Day Engagement</span>
                ) : (
                  <span data-testid="dfy-status-declined" className={mutedBadge}>Not Approved</span>
                )
              )}

              {a.community && (
                user && user.community_access ? (
                  communityLink ? (
                    <a href={communityLink} target="_blank" rel="noopener noreferrer" data-testid="community-link" className={darkBtn}>
                      Open Community <ArrowRight size={14} />
                    </a>
                  ) : (
                    <span data-testid="community-member-badge" className={accentBadge}>Member — invite link coming soon</span>
                  )
                ) : user && (user.packages?.length > 0 || user.package) ? (
                  <button data-testid={`community-join-${i}`} onClick={joinCommunity} className={darkBtn}>
                    Join Free — Included <ArrowRight size={14} />
                  </button>
                ) : (
                  <button data-testid={`community-join-${i}`} onClick={() => startCheckout("community_access", user, navigate)} className={darkBtn}>
                    Join — $100 <ArrowRight size={14} />
                  </button>
                )
              )}

              {a.comingSoon && (
                <span data-testid="coming-soon-badge" className={mutedBadge}>Coming Soon</span>
              )}

              {!a.highlight && !a.buy && !a.dfy && !a.community && !a.comingSoon && (
                <button
                  data-testid={`service-request-${i}`}
                  onClick={() => openRequest(a)}
                  className={outlineBtn}
                >
                  Request This Service <ArrowRight size={14} />
                </button>
              )}
            </motion.div>
          ))}
        </div>

        <div className="mt-12">
          <Link
            to="/#packages"
            data-testid="services-packages-link"
            className="inline-block border border-foreground bg-foreground px-8 py-4 text-xs uppercase tracking-[0.15em] text-background transition-colors duration-300 hover:bg-primary hover:border-primary hover:text-primary-foreground"
          >
            View Membership Packages
          </Link>
        </div>

        {/* Generic request dialog */}
        <Dialog open={!!requestFor} onOpenChange={() => setRequestFor(null)}>
          <DialogContent className="rounded-none border-border bg-white" data-testid="service-request-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-light">Request: {requestFor?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input data-testid="service-request-name" placeholder="Your name" value={reqName} onChange={(e) => setReqName(e.target.value)} className="rounded-none border-input" />
              <Input data-testid="service-request-email" type="email" placeholder="Your email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} className="rounded-none border-input" />
              <Textarea data-testid="service-request-note" placeholder="Tell Ciara what you need help with..." value={reqNote} onChange={(e) => setReqNote(e.target.value)} className="min-h-[100px] rounded-none border-input" />
              <Button data-testid="service-request-submit" onClick={submitRequest} disabled={sending} className="w-full rounded-none bg-foreground py-5 text-xs uppercase tracking-[0.15em] text-background hover:bg-primary hover:text-primary-foreground">
                {sending ? "Sending..." : "Send Request"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* DFY application dialog */}
        <Dialog open={dfyOpen} onOpenChange={setDfyOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-border bg-white sm:max-w-2xl" data-testid="dfy-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl font-light">Done-for-You Application — $800</DialogTitle>
            </DialogHeader>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Ciara reviews every application before payment. Once approved, you'll complete checkout and a 30-minute consultation will be scheduled to review your worksheet and set your plan — then ongoing communication for the 30-day engagement.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {DFY_FIELDS.map((f) => (
                <div key={f.key} className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.15em]">{f.label}</Label>
                  <Input
                    data-testid={`dfy-input-${f.key}`}
                    type={f.type || "text"}
                    placeholder={f.ph}
                    value={dfyForm[f.key]}
                    onChange={(e) => setDfyForm({ ...dfyForm, [f.key]: e.target.value })}
                    className="rounded-none border-input"
                  />
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em]">Additional comments</Label>
              <Textarea
                data-testid="dfy-input-comments"
                placeholder="Anything else Ciara should know..."
                value={dfyForm.comments}
                onChange={(e) => setDfyForm({ ...dfyForm, comments: e.target.value })}
                className="min-h-[90px] rounded-none border-input"
              />
            </div>
            <Button data-testid="dfy-submit" onClick={submitDfy} disabled={dfySending} className="w-full rounded-none bg-foreground py-5 text-xs uppercase tracking-[0.15em] text-background hover:bg-primary hover:text-primary-foreground">
              {dfySending ? "Submitting..." : "Submit Application for Review"}
            </Button>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
